import React, { useState, useMemo } from "react";
import { Modal } from "react-bootstrap";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart, BarElement, ArcElement, CategoryScale, LinearScale,
  PointElement, LineElement, Tooltip, Legend, ChartEvent, ActiveElement,
} from "chart.js";

Chart.register(BarElement, ArcElement, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

// ─── Design Tokens ────────────────────────────────────────────────────────────
const T = {
  bg:          "#eef1f6", bgCard:"#ffffff", bgSidebar:"#f5f7fa",
  textPrimary: "#0f1923", textSecond:"#1e2d3d", textMuted:"#445566", textLabel:"#2c3e52",
  brand:       "#1e3a6e", brandLight:"#dde7f5", brandMid:"#90aed0", border:"#c8d4e0",
  green:       "#166534", greenBg:"#f0fdf4", greenBorder:"#86efac",
  amber:       "#7c2d00", amberBg:"#fff7ed", amberBorder:"#fde68a",
  red:         "#991b1b", redBg:"#fef2f2",   redBorder:"#fca5a5",
  purple:      "#6b21a8", purpleBg:"#faf5ff", purpleBorder:"#d8b4fe",
  slate:       "#334155", slateBg:"#f8fafc",  slateBorder:"#cbd5e1",
};

// ─── Muted chart palette ──────────────────────────────────────────────────────
// Desaturated — no neon, no jarring primaries
const CHART_PAL: Record<string,string> = {
  PASS:    "#5a9c75",   // muted sage green
  FAIL:    "#b85c5a",   // dusty rose-red
  ERROR:   "#8870b0",   // dusty lavender
  SKIPPED: "#b09050",   // warm muted amber/gold
  UNKNOWN: "#7a8fa6",   // steel blue-grey
  APPLIED: "#4487a4",   // calm teal-blue
  NO_OP:   "#a8b4be",   // neutral grey
};

// ─── Status metadata with user-facing descriptions ───────────────────────────
const STATUS_META: Record<string,{ color:string; bg:string; border:string; icon:string; desc:string }> = {
  PASS:    { color:"#2e7d52", bg:"#f0fdf4", border:"#a7d7bb", icon:"✓", desc:"Validated successfully" },
  FAIL:    { color:"#a02020", bg:"#fef2f2", border:"#f0a8a8", icon:"✕", desc:"Validation failed — records need attention" },
  SKIPPED: { color:"#885c10", bg:"#fffbeb", border:"#e8cc78", icon:"⊘", desc:"Skipped — lookup / reference data unavailable" },
  UNKNOWN: { color:"#4a5e70", bg:"#f4f7fa", border:"#b0c2d0", icon:"?", desc:"Result undetermined" },
  ERROR:   { color:"#604898", bg:"#faf5ff", border:"#c8b0e8", icon:"!", desc:"Execution error during rule evaluation" },
  APPLIED: { color:"#1a658a", bg:"#ecfeff", border:"#88cce0", icon:"★", desc:"Cleansing rule applied — data fixed automatically" },
  NO_OP:   { color:"#526070", bg:"#f4f6f8", border:"#b8c8d4", icon:"–", desc:"No opportunity — no matching data found to fix" },
  "N/A":   { color:"#6a7880", bg:"#f4f6f8", border:"#b8c8d4", icon:"—", desc:"Not applicable" },
};

const SEV_META: Record<string,{ label:string; color:string; bg:string }> = {
  H:      { label:"HIGH", color:"#a02020", bg:"#fef2f2" },
  HIGH:   { label:"HIGH", color:"#a02020", bg:"#fef2f2" },
  M:      { label:"MED",  color:"#885c10", bg:"#fffbeb" },
  MEDIUM: { label:"MED",  color:"#885c10", bg:"#fffbeb" },
  L:      { label:"LOW",  color:"#2e7d52", bg:"#f0fdf4" },
  LOW:    { label:"LOW",  color:"#2e7d52", bg:"#f0fdf4" },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtN = (n?: number | null) => (n != null ? n.toLocaleString() : "—");

/**
 * Pass Rate = PASS / (PASS + FAIL)
 * SKIPPED / NO_OP / APPLIED / UNKNOWN are intentionally excluded from denominator:
 *  • SKIPPED  — couldn't run (no lkp data), not a result
 *  • NO_OP    — std rule, nothing to fix → neutral
 *  • APPLIED  — std rule, data fixed → success but counted separately
 *  • UNKNOWN  — ambiguous
 */
const calcPassRate = (counts: Record<string,number>): number | null => {
  const p = counts.PASS  || 0;
  const f = counts.FAIL  || 0;
  const e = counts.ERROR || 0;
  const d = p + f; // + e;
  return d ? +((p / d) * 100).toFixed(1) : null;
};

interface RuleFilters { status:string; severity:string; dimension:string; source:string; column:string; }

// ─── Sub-components ───────────────────────────────────────────────────────────
const SectionHeader = ({ title, badge }: { title:string; badge?: React.ReactNode }) => (
  <div style={{ display:"flex", alignItems:"center", gap:9, marginBottom:12 }}>
    <span style={{ width:3, height:15, borderRadius:2, background:`linear-gradient(180deg,${T.brand},${T.brandMid})`, flexShrink:0 }} />
    <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.07em", textTransform:"uppercase", color:T.textLabel }}>{title}</span>
    {badge && <span style={{ marginLeft:"auto" }}>{badge}</span>}
  </div>
);

const StatPill = ({ label, value, color, bg, bdr }: { label:string; value:string|number; color:string; bg:string; bdr:string }) => (
  <div style={{ display:"flex", flexDirection:"column", alignItems:"center", background:bg, border:`1px solid ${bdr}`, borderRadius:7, padding:"9px 14px", minWidth:74 }}>
    <span style={{ fontSize:19, fontWeight:800, color, lineHeight:1.1 }}>{value}</span>
    <span style={{ fontSize:9, fontWeight:700, color, opacity:.65, marginTop:3, textTransform:"uppercase", letterSpacing:"0.07em", textAlign:"center", whiteSpace:"nowrap" }}>{label}</span>
  </div>
);

const StatusChip = ({ status }: { status:string }) => {
  const s = (status||"UNKNOWN").toUpperCase();
  const m = STATUS_META[s] || STATUS_META.UNKNOWN;
  return (
    <span title={m.desc} style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"2px 7px", borderRadius:20, fontSize:10, fontWeight:700, color:m.color, background:m.bg, border:`1px solid ${m.border}`, cursor:"default", whiteSpace:"nowrap" }}>
      <span style={{ fontSize:9 }}>{m.icon}</span>{s}
    </span>
  );
};

const SevChip = ({ sev }: { sev:string }) => {
  const m = SEV_META[(sev||"").toUpperCase()] || { label:sev||"—", color:T.textMuted, bg:T.slateBg };
  return (
    <span style={{ padding:"2px 6px", borderRadius:4, fontSize:9, fontWeight:700, color:m.color, background:m.bg, border:`1px solid ${m.color}30`, letterSpacing:"0.05em" }}>
      {m.label}
    </span>
  );
};

const DonutScore = ({ passRate, size=128 }: { passRate:number|null; size?:number }) => {
  const r    = (size/2) - 11;
  const circ = 2 * Math.PI * r;
  const color = passRate == null ? T.border : passRate >= 75 ? "#5a9c75" : passRate >= 50 ? "#b09050" : "#b85c5a";
  const dash  = passRate != null ? circ * (passRate/100) : 0;
  return (
    <div style={{ position:"relative", width:size, height:size, flexShrink:0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={T.border} strokeWidth={8} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={8}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          transform={`rotate(-90 ${size/2} ${size/2})`}
          style={{ transition:"stroke-dasharray 0.7s ease" }}
        />
      </svg>
      <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
        {passRate != null
          ? <><span style={{ fontSize:21, fontWeight:800, color, lineHeight:1 }}>{passRate}%</span>
              <span style={{ fontSize:8, fontWeight:700, color:T.textMuted, textTransform:"uppercase", letterSpacing:"0.07em", marginTop:2 }}>Pass Rate</span></>
          : <span style={{ fontSize:10, color:T.textMuted, textAlign:"center" }}>No<br/>result</span>
        }
      </div>
    </div>
  );
};

// ─── Stacked-pct bar data ─────────────────────────────────────────────────────
const makeStackedPct = (countsObj: Record<string,any>) => {
  const labels = Object.keys(countsObj);
  const safe = (item:any, k:string) => item?.[k] || 0;
  const tot  = (item:any) => {
    const t = item?.total || Object.values(item||{}).reduce((a:number,v:any)=>a+(+v||0),0);
    return (t as number) || 1;
  };
  const pOf  = (item:any, k:string) => +((safe(item,k)/tot(item))*100).toFixed(1);
  return {
    labels,
    datasets: [
      { label:"PASS",    data:labels.map(k=>pOf(countsObj[k],"PASS")),    backgroundColor:CHART_PAL.PASS,    borderWidth:0 },
      { label:"FAIL",    data:labels.map(k=>pOf(countsObj[k],"FAIL")),    backgroundColor:CHART_PAL.FAIL,    borderWidth:0 },
      { label:"ERROR",   data:labels.map(k=>pOf(countsObj[k],"ERROR")),   backgroundColor:CHART_PAL.ERROR,   borderWidth:0 },
      { label:"SKIPPED", data:labels.map(k=>pOf(countsObj[k],"SKIPPED")), backgroundColor:CHART_PAL.SKIPPED, borderWidth:0 },
      { label:"UNKNOWN", data:labels.map(k=>pOf(countsObj[k],"UNKNOWN")), backgroundColor:CHART_PAL.UNKNOWN, borderWidth:0 },
      { label:"APPLIED", data:labels.map(k=>pOf(countsObj[k],"APPLIED")), backgroundColor:CHART_PAL.APPLIED, borderWidth:0 },
      { label:"NO_OP",   data:labels.map(k=>pOf(countsObj[k],"NO_OP")),   backgroundColor:CHART_PAL.NO_OP,   borderWidth:0 },
    ],
  };
};

const hBarOpts = (onClick:(e:ChartEvent,el:ActiveElement[])=>void) => ({
  indexAxis:"y" as const, responsive:true, maintainAspectRatio:false,
  plugins:{
    legend:{ position:"top" as const, labels:{ color:T.textSecond, font:{size:10}, boxWidth:10, padding:8 } },
    tooltip:{ mode:"index" as const, intersect:false, callbacks:{ label:(i:any)=>`${i.dataset.label}: ${i.formattedValue}%` } },
  },
  scales:{
    x:{ stacked:true, min:0, max:100, ticks:{ color:T.textMuted, font:{size:10}, callback:(v:any)=>`${v}%` }, grid:{ color:`${T.border}55` } },
    y:{ stacked:true, ticks:{ color:T.textSecond, font:{size:11} }, grid:{ display:false } },
  },
  onClick,
});

// ─── Scrollable column chart ──────────────────────────────────────────────────
/**
 * Fixed 24px per row. Container caps at MAX_VIS then scrolls.
 * Works for 5 columns or 200+ columns equally well.
 */
const ColumnChart = ({ data, colCount, onBarClick }:{
  data:ReturnType<typeof makeStackedPct>; colCount:number;
  onBarClick:(e:ChartEvent,el:ActiveElement[])=>void;
}) => {
  const ROW_H   = 24;
  const LEGEND  = 52;
  const MIN_H   = 200;
  const MAX_VIS = 330;
  const chartH  = Math.max(MIN_H, colCount * ROW_H + LEGEND);
  const contH   = Math.min(MAX_VIS, chartH);

  return (
    <div style={{ height:contH, overflowY:chartH > MAX_VIS ? "auto" : "hidden", overflowX:"hidden",
      borderRadius:5, scrollbarWidth:"thin", scrollbarColor:`${T.border} transparent` }}>
      <div style={{ height:chartH, position:"relative" }}>
        <Bar data={data} options={hBarOpts(onBarClick)} />
      </div>
    </div>
  );
};

// ─── Rule card ────────────────────────────────────────────────────────────────
const RuleCard = ({ r, onClick }:{ r:any; onClick:()=>void }) => {
  const status   = (r.result_status || r.status || "UNKNOWN").toUpperCase();
  const severity = r.rule_details?.sev || r.severity || "";
  const desc     = r.rule_details?.d || r.rule_details?.n || "";
  const col      = r.column_name || r.column || "—";
  const dim      = r.rule_details?.dq_dim || r.dimension || "—";
  const sm       = STATUS_META[status] || STATUS_META.UNKNOWN;
  const failCnt  = r.validation_results?.fail_count;
  const affCnt   = r.validation_results?.examples?.affected_count;

  return (
    <div onClick={onClick} style={{
      background:T.bgCard, border:`1px solid ${T.border}`,
      borderLeft:`3px solid ${sm.color}`,
      borderRadius:8, padding:"10px 12px", cursor:"pointer",
      transition:"box-shadow 0.15s, border-color 0.15s",
    }}
    onMouseEnter={e=>{ const d=e.currentTarget as HTMLDivElement; d.style.boxShadow="0 3px 10px rgba(30,58,110,0.11)"; d.style.borderColor=T.brand; }}
    onMouseLeave={e=>{ const d=e.currentTarget as HTMLDivElement; d.style.boxShadow="none"; d.style.borderColor=T.border; }}
    >
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:6, marginBottom:5 }}>
        <span style={{ fontSize:11, fontWeight:700, color:T.brand, fontFamily:"monospace", lineHeight:1.3 }}>
          {r.rule_id || r.id}
        </span>
        <div style={{ display:"flex", gap:3, flexShrink:0, flexWrap:"wrap", justifyContent:"flex-end" }}>
          <StatusChip status={status} />
          <SevChip sev={severity} />
        </div>
      </div>
      <div style={{ fontSize:10, color:T.textMuted, marginBottom:6, lineHeight:1.45 }}>
        {desc.length > 70 ? desc.slice(0,70)+"…" : desc || "Click for details"}
      </div>
      <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
        <span style={{ fontSize:9, background:T.brandLight, color:T.brand, padding:"1px 5px", borderRadius:3, fontWeight:600 }}>{dim}</span>
        <span style={{ fontSize:9, color:T.textMuted, background:T.slateBg, padding:"1px 5px", borderRadius:3 }}>{col}</span>
        {(failCnt != null && failCnt > 0) && (
          <span style={{ fontSize:9, color:"#a02020", background:"#fef2f2", padding:"1px 5px", borderRadius:3, fontWeight:600 }}>{fmtN(failCnt)} fails</span>
        )}
        {(affCnt != null && affCnt > 0) && (
          <span style={{ fontSize:9, color:"#1a658a", background:"#ecfeff", padding:"1px 5px", borderRadius:3, fontWeight:600 }}>{fmtN(affCnt)} fixed</span>
        )}
      </div>
    </div>
  );
};

// ─── Filter select ────────────────────────────────────────────────────────────
const FilterSel = ({ value, onChange, placeholder, options }:{
  value:string; onChange:(v:string)=>void; placeholder:string; options:{label:string;count:number}[];
}) => (
  <select value={value} onChange={e=>onChange(e.target.value)} style={{
    height:28, padding:"0 8px", fontSize:11, fontWeight:500, color:T.textSecond,
    background:T.bgCard, border:`1px solid ${value ? T.brand : T.border}`,
    borderRadius:5, outline:"none", cursor:"pointer",
    boxShadow:value ? `0 0 0 2px ${T.brandLight}` : "none",
  }}>
    <option value="">{placeholder}</option>
    {options.map(o=>(
      <option key={o.label} value={o.label}>{o.label} ({o.count})</option>
    ))}
  </select>
);

// ─── Rule detail modal ────────────────────────────────────────────────────────
const RuleModal = ({ rule, onClose }:{ rule:any; onClose:()=>void }) => {
  const status = (rule.result_status || rule.status || "UNKNOWN").toUpperCase();
  const sev    = rule.rule_details?.sev || rule.severity || "";
  const isStd  = rule.rule_details?.cat === "std";
  const vr     = rule.validation_results || {};
  const sm     = STATUS_META[status] || STATUS_META.UNKNOWN;

  const TR = ({ label, value }:{ label:string; value:React.ReactNode }) => (
    <tr>
      <td style={{ padding:"7px 10px", fontWeight:600, fontSize:11, color:T.textLabel, width:"28%", background:T.slateBg, borderBottom:`1px solid ${T.border}`, verticalAlign:"top" }}>{label}</td>
      <td style={{ padding:"7px 10px", fontSize:11, color:T.textSecond, borderBottom:`1px solid ${T.border}` }}>{value}</td>
    </tr>
  );

  return (
    <Modal show onHide={onClose} size="lg" centered>
      <div style={{ background:T.bgCard, borderRadius:10, overflow:"hidden", border:`1px solid ${T.border}` }}>
        <div style={{ background:`linear-gradient(135deg,${T.brand} 0%,#2d5aa8 100%)`, padding:"15px 20px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <div style={{ fontSize:9, color:"rgba(255,255,255,0.5)", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:3 }}>Rule Detail</div>
            <div style={{ fontSize:14, fontWeight:700, color:"#fff", fontFamily:"monospace" }}>{rule.rule_id}</div>
          </div>
          <div style={{ display:"flex", gap:6, alignItems:"center" }}>
            <StatusChip status={status} />
            <SevChip sev={sev} />
            <button onClick={onClose} style={{ background:"rgba(255,255,255,0.15)", border:"none", color:"#fff", borderRadius:5, width:24, height:24, cursor:"pointer", fontSize:13, lineHeight:1 }}>✕</button>
          </div>
        </div>

        <div style={{ padding:"16px 20px", overflowY:"auto", maxHeight:"66vh" }}>
          {/* Status banner */}
          <div style={{ background:sm.bg, border:`1px solid ${sm.border}`, borderRadius:6, padding:"7px 11px", marginBottom:12, display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:15, color:sm.color }}>{sm.icon}</span>
            <span style={{ fontSize:11, color:sm.color, fontWeight:700 }}>{status}</span>
            <span style={{ fontSize:11, color:T.textMuted }}>— {sm.desc}</span>
          </div>

          {/* Counts */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:14 }}>
            <StatPill label="Total Records"              value={fmtN(vr.total_records)}                                  color={T.brand}  bg={T.brandLight} bdr={T.border} />
            <StatPill label={isStd?"Rows Fixed":"Failing"} value={fmtN(isStd?vr.examples?.affected_count:vr.fail_count)} color={isStd?"#1a658a":"#a02020"} bg={isStd?"#ecfeff":"#fef2f2"} bdr={isStd?"#88cce0":"#f0a8a8"} />
            <StatPill label="Passed"                     value={fmtN(vr.passed_count)}                                  color="#2e7d52"  bg="#f0fdf4"       bdr="#a7d7bb" />
          </div>

          {/* Error */}
          {status !== "PASS" && vr.sample_failures?.length > 0 && (
            <div style={{ marginBottom:12 }}>
              <SectionHeader title="Root Cause" />
              <div style={{ background:T.redBg, border:`1px solid ${T.redBorder}`, borderRadius:5, padding:"8px 11px", fontSize:11, color:"#a02020", lineHeight:1.5 }}>
                {vr.sample_failures[0].error || vr.sample_failures[0].failure_reason || "No specific message."}
              </div>
            </div>
          )}

          {/* Definition */}
          <div style={{ marginBottom:12 }}>
            <SectionHeader title="Rule Definition" />
            <table style={{ width:"100%", borderCollapse:"collapse", border:`1px solid ${T.border}`, borderRadius:6, overflow:"hidden" }}>
              <tbody>
                <TR label="DQ Dimension"   value={<span style={{ background:T.brandLight, color:T.brand, padding:"2px 6px", borderRadius:3, fontSize:10, fontWeight:600 }}>{rule.rule_details?.dq_dim || rule.dimension || "—"}</span>} />
                <TR label="Severity"       value={<SevChip sev={sev} />} />
                <TR label="Source"         value={<code style={{ fontSize:10 }}>{rule.source_prefix||"—"}</code>} />
                <TR label="Column"         value={<code style={{ fontSize:10 }}>{rule.column_name||rule.column||"—"}</code>} />
                <TR label="Description"    value={rule.rule_details?.d||rule.rule_details?.n||"—"} />
                <TR label="Business Impact" value={rule.rule_details?.bi||"—"} />
              </tbody>
            </table>
          </div>

          {/* Logic */}
          <div style={{ marginBottom:12 }}>
            <SectionHeader title="Logic & Remediation" />
            <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
              {!isStd && rule.rule_details?.vl && (
                <div>
                  <div style={{ fontSize:10, fontWeight:600, color:T.textLabel, marginBottom:3 }}>Validation Logic</div>
                  <code style={{ display:"block", background:T.slateBg, border:`1px solid ${T.border}`, borderRadius:5, padding:"6px 9px", fontSize:10, color:T.textSecond }}>{rule.rule_details.vl}</code>
                </div>
              )}
              <div style={{ display:"flex", gap:12, flexWrap:"wrap", alignItems:"center" }}>
                <span style={{ fontSize:11, color:T.textLabel, fontWeight:600 }}>Action:</span>
                <span style={{ fontSize:10, background:T.slateBg, color:T.slate, padding:"2px 6px", borderRadius:4, fontWeight:600, border:`1px solid ${T.border}` }}>{rule.rule_details?.act||"—"}</span>
                <span style={{ fontSize:11, color:T.textLabel, fontWeight:600 }}>Fix:</span>
                <span style={{ fontSize:11, color:"#2e7d52" }}>{rule.rule_details?.fix||"—"}</span>
              </div>
              {(isStd ? rule.rule_details?.te : rule.rule_details?.ve) && (
                <div>
                  <div style={{ fontSize:10, fontWeight:600, color:T.textLabel, marginBottom:3 }}>{isStd?"Transformation Expression":"Validation Expression"}</div>
                  <pre style={{ background:T.slateBg, border:`1px solid ${T.border}`, borderRadius:5, padding:"8px 10px", fontSize:10, overflowX:"auto", margin:0, color:T.textSecond }}>
                    {JSON.stringify(isStd ? rule.rule_details.te : rule.rule_details.ve, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>

          {/* Samples */}
          <div>
            <SectionHeader title={isStd?"Sample Changes":"Sample Failures"} />
            <pre style={{ background:T.slateBg, border:`1px solid ${T.border}`, borderRadius:5, padding:"8px 10px", fontSize:10, overflowX:"auto", maxHeight:160, margin:0, color:T.textSecond }}>
              {JSON.stringify(isStd ? vr.examples?.sample_changes : vr.sample_failures, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </Modal>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function DQDashboard({ dq, rules }: { dq:any; rules:any[] }) {
  const summary     = dq?.dq_core || {};
  const stdRules    = useMemo(() => rules.filter(r => r.rule_details?.cat === "std"), [rules]);
  const nonStdRules = useMemo(() => rules.filter(r => r.rule_details?.cat !== "std"), [rules]);

  const [activeTab,      setActiveTab]      = useState<"NON_STD"|"STD">("NON_STD");
  const [selectedFilter, setSelectedFilter] = useState<any>(null);
  const [selectedRule,   setSelectedRule]   = useState<any>(null);
  const [ruleFilters,    setRuleFilters]    = useState<RuleFilters>({ status:"", severity:"", dimension:"", source:"", column:"" });
  const [showStatusKey,  setShowStatusKey]  = useState(false);

  const activeRules   = activeTab === "STD" ? stdRules : nonStdRules;
  const statusCounts  = (dq?.overall_counts?.execution_status || {}) as Record<string,number>;
  const dimCounts     = activeTab === "STD" ? dq?.std_section?.by_dim   || {} : summary?.by_dim   || {};
  const colCounts     = activeTab === "STD" ? dq?.std_section?.by_column || {} : summary?.by_column || {};

  const passRate   = calcPassRate(statusCounts);
  const totalRules = Object.values(statusCounts).reduce((a,v)=>a+(v||0), 0) as number;

  // pie
  const pieLabels = Object.keys(statusCounts).filter(k => statusCounts[k] > 0);
  const pieData   = {
    labels: pieLabels,
    datasets:[{ data:pieLabels.map(l=>statusCounts[l]), backgroundColor:pieLabels.map(l=>CHART_PAL[l]||T.textMuted), borderWidth:2, borderColor:T.bgCard }],
  };

  // faceted filter
  const getRV = (r:any, field:string) => {
    switch(field){
      case "status":    return (r.result_status||r.status||"UNKNOWN").toUpperCase();
      case "severity":  return (r.severity||r.rule_details?.sev||"").toUpperCase();
      case "dimension": return (r.dimension||r.rule_details?.dq_dim||"").toUpperCase();
      case "source":    return (r.source_prefix||"").toUpperCase();
      case "column":    return (r.column||r.rule_details?.column_name||"").toUpperCase();
      default:          return "";
    }
  };

  const facetedOpts = (fk: keyof RuleFilters) => {
    const rel = activeRules.filter(r =>
      Object.keys(ruleFilters).every(k => {
        if (k === fk) return true;
        const fv = ruleFilters[k as keyof RuleFilters];
        return !fv || getRV(r,k) === fv.toUpperCase();
      })
    );
    const cnt: Record<string,number> = {};
    rel.forEach(r => { const v=getRV(r,fk); if(v) cnt[v]=(cnt[v]||0)+1; });
    return Object.entries(cnt).sort((a,b)=>b[1]-a[1]).map(([label,count])=>({ label, count }));
  };

  const filteredRules = useMemo(() => {
    let rs = activeRules;
    if (selectedFilter) {
      const fv=(selectedFilter.value||"").toString().toUpperCase();
      rs=rs.filter(r=>{
        switch(selectedFilter.type){
          case "status":    return getRV(r,"status")    === fv;
          case "dimension": return getRV(r,"dimension") === fv;
          case "column":    return getRV(r,"column")    === fv;
          default:          return true;
        }
      });
    }
    return rs.filter(r=>Object.keys(ruleFilters).every(k=>{
      const fv=ruleFilters[k as keyof RuleFilters];
      return !fv||getRV(r,k)===fv.toUpperCase();
    }));
  }, [selectedFilter, activeRules, ruleFilters]);

  const clearFilters = () => { setRuleFilters({ status:"", severity:"", dimension:"", source:"", column:"" }); setSelectedFilter(null); };
  const hasFilters   = Object.values(ruleFilters).some(Boolean) || selectedFilter;

  const handleBarClick = (type:string, labels:string[]) => (e:ChartEvent, elems:ActiveElement[]) => {
    if (!elems?.length) return;
    setSelectedFilter({ type, value:labels[elems[0].index] });
    setRuleFilters({ status:"", severity:"", dimension:"", source:"", column:"" });
  };

  const dimData  = makeStackedPct(dimCounts);
  const colData  = makeStackedPct(colCounts);
  const colCount = Object.keys(colCounts).length;

  return (
    <>
      <div style={{ fontFamily:"'Inter','Segoe UI',sans-serif", color:T.textPrimary }}>

        {/* ── Overall Summary ─────────────────────────────────────────── */}
        <div style={{ background:T.bgCard, border:`1px solid ${T.border}`, borderRadius:10, padding:"16px 20px", marginBottom:16, boxShadow:"0 2px 8px rgba(0,0,0,0.05)" }}>
          <SectionHeader title="Overall Data Quality"
            badge={
              <button onClick={()=>setShowStatusKey(p=>!p)}
                style={{ fontSize:10, color:T.brand, background:T.brandLight, border:`1px solid ${T.border}`, borderRadius:5, padding:"2px 8px", cursor:"pointer", fontWeight:600 }}>
                {showStatusKey ? "Hide Key" : "Status Key"}
              </button>
            }
          />

          {/* Status key */}
          {showStatusKey && (
            <div style={{ background:T.slateBg, border:`1px solid ${T.border}`, borderRadius:7, padding:"10px 14px", marginBottom:12 }}>
              <div style={{ fontSize:10, fontWeight:700, color:T.textLabel, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:6 }}>Status Definitions</div>
              {["PASS","FAIL","ERROR","SKIPPED","UNKNOWN","APPLIED","NO_OP"].map(s => {
                const m = STATUS_META[s];
                return (
                  <div key={s} style={{ display:"flex", alignItems:"flex-start", gap:8, padding:"4px 0", borderBottom:`1px solid ${T.border}` }}>
                    <span style={{ width:6, height:6, borderRadius:"50%", background:m.color, flexShrink:0, marginTop:3 }} />
                    <span style={{ fontSize:11, fontWeight:700, color:m.color, minWidth:68 }}>{s}</span>
                    <span style={{ fontSize:11, color:T.textMuted }}>{m.desc}</span>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ display:"flex", alignItems:"center", gap:22, flexWrap:"wrap" }}>
            <DonutScore passRate={passRate} />

            {/* Mini pie */}
            <div style={{ width:120, height:110, flexShrink:0 }}>
              <Pie data={pieData} options={{
                cutout:"58%", responsive:true, maintainAspectRatio:false,
                plugins:{
                  legend:{ display:false },
                  tooltip:{ callbacks:{ label:(i)=>`${i.label}: ${i.formattedValue} (${totalRules?((i.raw as number/totalRules)*100).toFixed(1):0}%)` } },
                },
                onClick:(_,elems)=>{
                  if(elems?.length){ setSelectedFilter({ type:"status", value:pieLabels[elems[0].index] }); setRuleFilters({ status:"", severity:"", dimension:"", source:"", column:"" }); }
                },
              }} />
            </div>

            <div style={{ flex:1, display:"flex", flexDirection:"column", gap:10 }}>
              <div style={{ display:"flex", gap:7, flexWrap:"wrap" }}>
                <StatPill label="Total"   value={rules.length}           color={T.brand}  bg={T.brandLight} bdr={T.border} />
                <StatPill label="Pass"    value={statusCounts.PASS||0}   color="#2e7d52"  bg="#f0fdf4"      bdr="#a7d7bb" />
                <StatPill label="Fail"    value={statusCounts.FAIL||0}   color="#a02020"  bg="#fef2f2"      bdr="#f0a8a8" />
                <StatPill label="Applied" value={statusCounts.APPLIED||0} color="#1a658a" bg="#ecfeff"      bdr="#88cce0" />
                <StatPill label="Skipped" value={statusCounts.SKIPPED||0} color="#885c10" bg="#fffbeb"      bdr="#e8cc78" />
                <StatPill label="No-Op"   value={(statusCounts.UNKNOWN||0)+(statusCounts.NO_OP||0)} color="#526070" bg="#f4f6f8" bdr="#b8c8d4" />
                <StatPill label="Error"   value={statusCounts.ERROR||0}  color="#604898"  bg="#faf5ff"      bdr="#c8b0e8" />
              </div>
              <div style={{ fontSize:10, color:T.textMuted, fontStyle:"italic" }}>
                ✦ Pass Rate = PASS ÷ (PASS + FAIL + ERROR) — SKIPPED, APPLIED, NO_OP & UNKNOWN excluded from denominator
              </div>
              <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                {pieLabels.map(l => (
                  <span key={l} style={{ display:"flex", alignItems:"center", gap:4, fontSize:10, color:T.textSecond, background:T.slateBg, padding:"2px 6px", borderRadius:20, border:`1px solid ${T.border}` }}>
                    <span style={{ width:5, height:5, borderRadius:"50%", background:CHART_PAL[l]||T.textMuted }} />
                    {l}: <strong>{statusCounts[l]||0}</strong>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Tab switcher ────────────────────────────────────────────── */}
        <div style={{ display:"flex", gap:0, marginBottom:14, background:T.bgCard, border:`1px solid ${T.border}`, borderRadius:7, padding:3, width:"fit-content" }}>
          {([["NON_STD","DQ Validation Rules"],["STD","Cleansing / Standardization"]] as const).map(([key,label])=>(
            <button key={key}
              onClick={()=>{ setActiveTab(key); setSelectedFilter(null); setRuleFilters({ status:"", severity:"", dimension:"", source:"", column:"" }); }}
              style={{ padding:"5px 14px", borderRadius:5, border:"none", cursor:"pointer", fontSize:11, fontWeight:600,
                background:activeTab===key ? T.brand : "transparent",
                color:activeTab===key ? "#fff" : T.textMuted, transition:"all 0.15s",
              }}>
              {label} <span style={{ marginLeft:4, fontSize:10, opacity:.65 }}>({(key==="STD"?stdRules:nonStdRules).length})</span>
            </button>
          ))}
        </div>

        {/* ── Charts ──────────────────────────────────────────────────── */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
          {/* Dimension chart */}
          <div style={{ background:T.bgCard, border:`1px solid ${T.border}`, borderRadius:10, padding:"13px 15px", boxShadow:"0 1px 5px rgba(0,0,0,0.04)" }}>
            <SectionHeader title="By DQ Dimension" />
            <div style={{ height:250, position:"relative" }}>
              <Bar data={dimData} options={hBarOpts(handleBarClick("dimension", Object.keys(dimCounts)))} />
            </div>
          </div>

          {/* Column chart — scrollable */}
          <div style={{ background:T.bgCard, border:`1px solid ${T.border}`, borderRadius:10, padding:"13px 15px", boxShadow:"0 1px 5px rgba(0,0,0,0.04)" }}>
            <SectionHeader title="By Column"
              badge={
                <span style={{ fontSize:9, color:T.textMuted, background:T.slateBg, padding:"2px 6px", borderRadius:10, border:`1px solid ${T.border}` }}>
                  {colCount} cols{colCount > 13 ? " · scroll ↕" : ""}
                </span>
              }
            />
            <ColumnChart data={colData} colCount={colCount} onBarClick={handleBarClick("column", Object.keys(colCounts))} />
          </div>
        </div>

        {/* ── Rules Panel ─────────────────────────────────────────────── */}
        <div style={{ background:T.bgCard, border:`1px solid ${T.border}`, borderRadius:10, padding:"13px 15px", boxShadow:"0 1px 5px rgba(0,0,0,0.04)" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:9 }}>
            <div style={{ display:"flex", alignItems:"center", gap:7, flexWrap:"wrap" }}>
              <span style={{ width:3, height:15, borderRadius:2, background:`linear-gradient(180deg,${T.brand},${T.brandMid})`, flexShrink:0 }} />
              <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.07em", textTransform:"uppercase", color:T.textLabel }}>Rules</span>
              <span style={{ fontSize:10, fontWeight:600, color:T.textMuted, background:T.brandLight, borderRadius:10, padding:"2px 7px", border:`1px solid ${T.border}` }}>
                {filteredRules.length} / {activeRules.length}
              </span>
              {selectedFilter && (
                <span onClick={()=>setSelectedFilter(null)} style={{
                  display:"flex", alignItems:"center", gap:4, fontSize:10,
                  background:T.brandLight, color:T.brand, padding:"2px 7px", borderRadius:20, border:`1px solid ${T.border}`, cursor:"pointer"
                }}>
                  Chart: {selectedFilter.type} = {selectedFilter.value} ✕
                </span>
              )}
            </div>
            {hasFilters && (
              <button onClick={clearFilters} style={{ fontSize:10, color:"#a02020", background:"#fef2f2", border:"1px solid #f0a8a8", borderRadius:5, padding:"3px 8px", cursor:"pointer", fontWeight:600 }}>
                Clear All
              </button>
            )}
          </div>

          {/* Filters */}
          <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:10, padding:"7px 9px", background:T.slateBg, borderRadius:6, border:`1px solid ${T.border}` }}>
            <FilterSel value={ruleFilters.status}    onChange={v=>setRuleFilters(p=>({...p,status:v}))}    placeholder="Status (All)"    options={facetedOpts("status")} />
            <FilterSel value={ruleFilters.severity}  onChange={v=>setRuleFilters(p=>({...p,severity:v}))}  placeholder="Severity (All)"  options={facetedOpts("severity")} />
            <FilterSel value={ruleFilters.dimension} onChange={v=>setRuleFilters(p=>({...p,dimension:v}))} placeholder="Dimension (All)" options={facetedOpts("dimension")} />
            <FilterSel value={ruleFilters.source}    onChange={v=>setRuleFilters(p=>({...p,source:v}))}    placeholder="Source (All)"    options={facetedOpts("source")} />
            <FilterSel value={ruleFilters.column}    onChange={v=>setRuleFilters(p=>({...p,column:v}))}    placeholder="Column (All)"    options={facetedOpts("column")} />
          </div>

          {/* Grid */}
          <div style={{ maxHeight:"56vh", overflowY:"auto", paddingRight:3, scrollbarWidth:"thin", scrollbarColor:`${T.border} transparent` }}>
            {filteredRules.length === 0
              ? <div style={{ textAlign:"center", padding:"36px 0", color:T.textMuted, fontSize:13 }}>
                  <div style={{ fontSize:26, marginBottom:7 }}>🔍</div>
                  No rules match the current filters.
                </div>
              : <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(272px,1fr))", gap:8 }}>
                  {filteredRules.map(r => <RuleCard key={r.id} r={r} onClick={()=>setSelectedRule(r)} />)}
                </div>
            }
          </div>
        </div>
      </div>

      {selectedRule && <RuleModal rule={selectedRule} onClose={()=>setSelectedRule(null)} />}
    </>
  );
}